import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../store';
import { fetchData, postData } from './api';
import { isForLens, lensParams, selectCurrentGroupId, selectLensGroupId, type LensMeta } from './groups_slice';

type AsyncStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

const isSeAdminUser = (state: RootState) => state.authentication.user?.burnout_role === 'se_admin';

const resolveGroupId = (state: RootState): number | undefined =>
  isSeAdminUser(state)
    ? selectCurrentGroupId(state) || undefined
    : (state.authentication.user as any)?.practice_group_ids?.[0];

export interface RewardHistoryItem {
  id: string;
  order_id: string;
  created_at: string;
  expires_at: string;
  value: {
    denomination: number;
    currency_code: string;
  };
  delivery: {
    method: string;
    status: string;
  };
  recipient: {
    email: string;
    name: string;
    phone: string;
  };
}

interface RewardLinkState {
  status: AsyncStatus;
  link: string | null;
  error: string | null;
}

interface GamificationState {
  totalPoints: number;
  /**
   * GET /api/user_points `points_earned_last_30_days`: points from every event
   * except redemptions, created in the last 30 days. Null until the endpoint
   * has answered with it, so an older backend never shows a made-up 0.
   */
  pointsEarnedLast30Days: number | null;
  userPointsStatus: AsyncStatus;
  /** GET /api/user_points has answered at least once this session. A refetch
   *  (MainPage fetches again on every mount) then keeps the last balance on
   *  screen instead of flashing a placeholder. */
  userPointsLoaded: boolean;
  /** The group lens (selectLensGroupId) the balance is for. */
  userPointsLensGroupId: number | null;
  earnedAchievements: any[];
  achievementsStatus: AsyncStatus;
  /** GET /api/user_achievements has answered at least once this session. */
  achievementsLoaded: boolean;
  /** The group lens the earned rows are for. */
  achievementsLensGroupId: number | null;
  modalOpen: boolean;
  modalData: Record<string, unknown>;
  modalQueue: Record<string, unknown>[];
  rewardsHistory: RewardHistoryItem[];
  rewardsHistoryStatus: AsyncStatus;
  rewardsHistoryError: string | null;
  rewardLinks: Record<string, RewardLinkState>;
}

const initialState: GamificationState = {
  totalPoints: 0,
  pointsEarnedLast30Days: null,
  userPointsStatus: 'idle',
  userPointsLoaded: false,
  userPointsLensGroupId: null,
  earnedAchievements: [],
  achievementsStatus: 'idle',
  achievementsLoaded: false,
  achievementsLensGroupId: null,
  modalOpen: false,
  modalData: {},
  modalQueue: [],
  rewardsHistory: [],
  rewardsHistoryStatus: 'idle',
  rewardsHistoryError: null,
  rewardLinks: {},
};

// user_points and user_achievements follow the group lens, so each answer
// records the lens it was asked for and one for another lens is dropped.
type LensThunkConfig = { state: RootState; fulfilledMeta: LensMeta; rejectedMeta: LensMeta };

export const fetchUserAchievements = createAsyncThunk<any[], void, LensThunkConfig>(
  'gamification/fetchUserAchievements',
  async (_, { getState, fulfillWithValue, rejectWithValue }) => {
    const meta = { lensGroupId: selectLensGroupId(getState()) };
    try {
      const rows = (await fetchData({ endpoint: 'user_achievements', filters: lensParams(meta.lensGroupId) })) as any[];
      return fulfillWithValue(rows, meta);
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to fetch user achievements', meta);
    }
  },
  {
    // Fetched once per session, but a failed load may be retried.
    condition: (_, { getState }) => {
      const status = getState().gamification.achievementsStatus;
      return status === 'idle' || status === 'failed';
    },
  }
);

export const fetchRewardsHistory = createAsyncThunk<RewardHistoryItem[], void, { state: RootState }>(
  'gamification/fetchRewardsHistory',
  async (_, { getState, rejectWithValue }) => {
    const state = getState();
    const seAdmin = isSeAdminUser(state);
    const groupId = resolveGroupId(state);
    if (seAdmin && !groupId) return rejectWithValue('No practice group selected');
    try {
      const raw = (await fetchData({
        endpoint: 'rewards/history',
        filters: seAdmin ? { group_id: groupId } : undefined,
      })) as { rewards?: RewardHistoryItem[] };
      return Array.isArray(raw?.rewards) ? raw.rewards : [];
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to fetch rewards history');
    }
  },
  {
    condition: (_, { getState }) => getState().gamification.rewardsHistoryStatus !== 'loading',
  }
);

interface UserPointsPayload {
  total_points: number;
  points_earned_last_30_days?: number;
}

export const fetchUserPoints = createAsyncThunk<UserPointsPayload, void, LensThunkConfig>(
  'gamification/fetchUserPoints',
  async (_, { getState, fulfillWithValue, rejectWithValue }) => {
    const meta = { lensGroupId: selectLensGroupId(getState()) };
    try {
      const points = (await fetchData({ endpoint: 'user_points', filters: lensParams(meta.lensGroupId) })) as UserPointsPayload;
      return fulfillWithValue(points, meta);
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to fetch user points', meta);
    }
  },
  {
    condition: (_, { getState }) => getState().gamification.userPointsStatus !== 'loading',
  }
);

export const seedUserPoints = createAsyncThunk<void, void, { state: RootState }>(
  'gamification/seedUserPoints',
  async (_, { rejectWithValue }) => {
    try {
      await postData({ endpoint: 'user_points/seed' });
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to seed points');
    }
  }
);

export const requestRewardLink = createAsyncThunk<string, string, { state: RootState }>(
  'gamification/requestRewardLink',
  async (rewardId, { getState, rejectWithValue }) => {
    const state = getState();
    const seAdmin = isSeAdminUser(state);
    const groupId = resolveGroupId(state);
    if (seAdmin && !groupId) return rejectWithValue('No practice group selected');
    try {
      const raw = (await postData({
        endpoint: `rewards/${rewardId}/link`,
        filters: seAdmin ? { group_id: groupId } : undefined,
      })) as { link: string };
      return raw.link;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.error || err.message || 'Unable to generate reward link');
    }
  }
);

const mergeAchievements = (existing: any[], incoming: any[]) => {
  const existingIds = new Set(existing.map((a: any) => a.id));
  return [...existing, ...incoming.filter((a: any) => !existingIds.has(a.id))];
};

/**
 * One row of GET /api/user_achievements (UserAchievementSerializer), which is
 * also the shape log events return as `achievements_earned`. A repeatable badge
 * (one per series, say) has one row per award, so rows are not badges.
 */
export interface EarnedAchievementRow {
  id: number;
  achievement_id?: number | null;
  awarded_at: string;
  achievement?: { id?: number | null; name?: string | null } | null;
}

const badgeKey = (row: EarnedAchievementRow): number | string | null =>
  row.achievement_id ?? row.achievement?.id ?? row.achievement?.name ?? null;

const awardedAtMs = (row: EarnedAchievementRow): number => Date.parse(row.awarded_at);

/**
 * Distinct badges earned: award rows collapsed by achievement, the way
 * /profile/achievements shows a repeatable badge once with an "x N" multiplier.
 */
export const countDistinctBadges = (rows: readonly EarnedAchievementRow[]): number =>
  new Set(rows.map(badgeKey).filter((key) => key !== null)).size;

/**
 * The badge most recently added to the collection: take each achievement's
 * FIRST award (earliest awarded_at, tie -> lowest id), then the newest of those
 * (latest awarded_at, tie -> highest id). Re-earning a repeatable badge does not
 * make it the newest, because it was already unlocked. Null when there is none.
 */
export const newestUnlockedBadge = <T extends EarnedAchievementRow>(rows: readonly T[]): T | null => {
  const firstAwards = new Map<number | string, T>();
  for (const row of rows) {
    const key = badgeKey(row);
    if (key === null || Number.isNaN(awardedAtMs(row))) continue;
    const first = firstAwards.get(key);
    const isEarlier = !first
      || awardedAtMs(row) < awardedAtMs(first)
      || (awardedAtMs(row) === awardedAtMs(first) && row.id < first.id);
    if (isEarlier) firstAwards.set(key, row);
  }

  let newest: T | null = null;
  for (const row of firstAwards.values()) {
    const isNewer = !newest
      || awardedAtMs(row) > awardedAtMs(newest)
      || (awardedAtMs(row) === awardedAtMs(newest) && row.id > newest.id);
    if (isNewer) newest = row;
  }
  return newest;
};

const gamificationSlice = createSlice({
  name: 'gamification',
  initialState,
  reducers: {
    applyLogEventRewards: (state, action: PayloadAction<{ achievements_earned?: any[]; points_earned?: any[] }>) => {
      const { achievements_earned, points_earned } = action.payload;
      const knownAwardIds = new Set(state.earnedAchievements.map((a: any) => a.id));
      const newAwards = (achievements_earned ?? []).filter((a: any) => !knownAwardIds.has(a.id));
      const eventPoints = (points_earned ?? []).reduce((sum: number, p: any) => sum + (p.points ?? 0), 0);
      // AchievementEngine#award writes an 'achievement_earned' user_points row
      // worth achievement.points_awarded for every new badge, but the log
      // response's points_earned carries only PointsService rows. Without this
      // the balance would read low until the next GET /api/user_points.
      const badgePoints = newAwards.reduce((sum: number, a: any) => {
        const awarded = Number(a?.achievement?.points_awarded);
        return sum + (Number.isFinite(awarded) && awarded > 0 ? awarded : 0);
      }, 0);
      const earned = eventPoints + badgePoints;
      if (earned !== 0) {
        state.totalPoints += earned;
        // Points a log event awards were just created, so they fall inside the
        // 30-day window too. Left alone while the figure is unknown.
        if (state.pointsEarnedLast30Days !== null) state.pointsEarnedLast30Days += earned;
      }
      if (achievements_earned?.length) {
        state.earnedAchievements = mergeAchievements(state.earnedAchievements, achievements_earned);
      }
    },
    updateGamificationModal: {
      reducer(state, action: PayloadAction<{ status: boolean; data: Record<string, unknown> }>) {
        if (action.payload.status && state.modalOpen) {
          state.modalQueue.push(action.payload.data);
        } else {
          state.modalOpen = action.payload.status;
          state.modalData = action.payload.data;
        }
      },
      prepare(status: boolean, data: Record<string, unknown>) {
        return { payload: { status, data } };
      },
    },
    dequeueGamificationModal(state) {
      const next = state.modalQueue.shift();
      if (next) {
        state.modalData = next;
      } else {
        state.modalOpen = false;
        state.modalData = {};
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserAchievements.pending, (state) => { state.achievementsStatus = 'loading'; })
      .addCase(fetchUserAchievements.fulfilled, (state, action) => {
        if (!isForLens(action, state.achievementsLensGroupId)) return;
        state.achievementsStatus = 'succeeded';
        state.achievementsLoaded = true;
        state.earnedAchievements = action.payload ?? [];
      })
      .addCase(fetchUserAchievements.rejected, (state, action) => {
        if (!isForLens(action, state.achievementsLensGroupId)) return;
        state.achievementsStatus = 'failed';
      });

    builder
      .addCase(fetchRewardsHistory.pending, (state) => { state.rewardsHistoryStatus = 'loading'; state.rewardsHistoryError = null; })
      .addCase(fetchRewardsHistory.fulfilled, (state, action) => { state.rewardsHistoryStatus = 'succeeded'; state.rewardsHistory = action.payload; })
      .addCase(fetchRewardsHistory.rejected, (state, action) => { state.rewardsHistoryStatus = 'failed'; state.rewardsHistoryError = (action.payload as string) ?? action.error.message ?? null; });

    builder
      .addCase(requestRewardLink.pending, (state, action) => {
        state.rewardLinks[action.meta.arg] = { status: 'loading', link: null, error: null };
      })
      .addCase(requestRewardLink.fulfilled, (state, action) => {
        state.rewardLinks[action.meta.arg] = { status: 'succeeded', link: action.payload, error: null };
      })
      .addCase(requestRewardLink.rejected, (state, action) => {
        state.rewardLinks[action.meta.arg] = { status: 'failed', link: null, error: (action.payload as string) ?? action.error.message ?? null };
      });

    builder
      .addCase(fetchUserPoints.pending, (state) => { state.userPointsStatus = 'loading'; })
      .addCase(fetchUserPoints.fulfilled, (state, action) => {
        if (!isForLens(action, state.userPointsLensGroupId)) return;
        state.userPointsStatus = 'succeeded';
        state.userPointsLoaded = true;
        state.totalPoints = action.payload.total_points;
        const last30 = action.payload.points_earned_last_30_days;
        state.pointsEarnedLast30Days = typeof last30 === 'number' && Number.isFinite(last30) ? last30 : null;
      })
      .addCase(fetchUserPoints.rejected, (state, action) => {
        if (!isForLens(action, state.userPointsLensGroupId)) return;
        state.userPointsStatus = 'failed';
      });

    builder.addMatcher(
      (action) => action.type === 'tremendous/redeemPoints/fulfilled',
      (state, action: any) => {
        const pointsSpent = action.payload.points_spent as number;
        state.totalPoints = Math.max(0, state.totalPoints - pointsSpent);
      }
    );
  },
});

export const { applyLogEventRewards, updateGamificationModal, dequeueGamificationModal } = gamificationSlice.actions;

/**
 * Resets the balance and the earned badges when they were loaded for a lens
 * other than `lensGroupId`, so they read as not loaded and are asked for again
 * (MainPage refetches on a lens change). The root reducer runs this after every
 * action; with no lens it changes nothing.
 */
export const keepLens = (state: GamificationState, lensGroupId: number | null): GamificationState => {
  const pointsStale = (state.userPointsLensGroupId ?? null) !== lensGroupId;
  const achievementsStale = (state.achievementsLensGroupId ?? null) !== lensGroupId;
  if (!pointsStale && !achievementsStale) return state;
  return {
    ...state,
    ...(pointsStale && {
      totalPoints: 0, pointsEarnedLast30Days: null, userPointsStatus: 'idle', userPointsLoaded: false, userPointsLensGroupId: lensGroupId,
    }),
    ...(achievementsStale && {
      earnedAchievements: [], achievementsStatus: 'idle', achievementsLoaded: false, achievementsLensGroupId: lensGroupId,
    }),
  };
};

export const getTotalPoints = (state: GamificationState) => state.totalPoints;
export const getPointsEarnedLast30Days = (state: GamificationState) => state.pointsEarnedLast30Days;
export const getUserPointsStatus = (state: GamificationState) => state.userPointsStatus;
export const getEarnedAchievements = (state: GamificationState) => state.earnedAchievements;
export const getAchievementsStatus = (state: GamificationState) => state.achievementsStatus;
/** Loaded at least once and the latest request hasn't failed: the badges held
 *  in state can be shown, including while a refetch runs. */
export const getAchievementsReady = (state: GamificationState) =>
  state.achievementsLoaded && state.achievementsStatus !== 'failed';
/** The same for the points balance. */
export const getUserPointsReady = (state: GamificationState) =>
  state.userPointsLoaded && state.userPointsStatus !== 'failed';
export const getModalOpen = (state: GamificationState) => state.modalOpen;
export const getModalData = (state: GamificationState) => state.modalData;
export const getModalQueue = (state: GamificationState) => state.modalQueue;
export const getRewardsHistory = (state: GamificationState): RewardHistoryItem[] => state.rewardsHistory;
export const getRewardsHistoryStatus = (state: GamificationState) => state.rewardsHistoryStatus;
export const getRewardsHistoryError = (state: GamificationState) => state.rewardsHistoryError;
export const getRewardLink = (state: GamificationState, rewardId: string): RewardLinkState | undefined => state.rewardLinks[rewardId];

export default gamificationSlice.reducer;
